import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Alert,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  useAppDispatch,
  useAppSelector,
  fetchServiceById,
  createInstance,
  showToast,
} from "../store";
import { DurationSelector } from "../components/services";
import { ServiceIcon } from "../components/common";
import { serviceService, MAX_ACTIVE_INSTANCES } from "../services";

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { selectedService, isLoading, error } = useAppSelector(
    (state) => state.services,
  );
  const { user } = useAppSelector((state) => state.auth);
  const { activeCount, isCreating } = useAppSelector(
    (state) => state.instances,
  );

  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchServiceById(parseInt(id)));
    }
  }, [id, dispatch]);

  // Set default duration when service loads
  useEffect(() => {
    if (selectedService && !selectedDuration) {
      const options = serviceService.getDurationOptions(selectedService);
      if (options.length > 0) {
        setSelectedDuration(options[0].hours);
      }
    }
  }, [selectedService, selectedDuration]);

  const handleBack = () => {
    navigate("/services");
  };

  const canCreate = activeCount < MAX_ACTIVE_INSTANCES;

  const handleLaunch = async () => {
    if (!selectedService || !selectedDuration || !user) return;

    const result = await dispatch(
      createInstance({
        userId: user.id,
        serviceId: selectedService.id,
        durationHours: selectedDuration,
      }),
    );

    if (createInstance.fulfilled.match(result)) {
      dispatch(
        showToast({
          message: "Instance is being provisioned...",
          severity: "success",
        }),
      );
      navigate("/instances");
    } else {
      const errorMessage =
        typeof result.payload === "string"
          ? result.payload
          : "Failed to create instance. Please try again.";
      dispatch(
        showToast({
          message: errorMessage,
          severity: "error",
        }),
      );
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Services
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!selectedService) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Services
        </Button>
        <Alert severity="warning">Service not found</Alert>
      </Box>
    );
  }

  const durationOptions = serviceService.getDurationOptions(selectedService);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <IconButton onClick={handleBack} sx={{ bgcolor: "background.paper" }}>
          <ArrowBackIcon />
        </IconButton>
        <ServiceIcon serviceName={selectedService.name} size={56} />
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {selectedService.name}
          </Typography>
          <Chip
            label={
              durationOptions.length +
              " duration" +
              (durationOptions.length > 1 ? "s" : "")
            }
            size="small"
            sx={{ mt: 0.5, bgcolor: "#f3f4f6", color: "#6b7280" }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 4,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left: Service Info */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "text.primary",
              }}
            >
              About this service
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
              }}
            >
              {selectedService.description ||
                "Deploy and manage " +
                  selectedService.name +
                  " instances on demand. Get instant access to a fully configured environment with auto-generated credentials."}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "text.primary",
              }}
            >
              What you get
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[
                "Instant provisioning",
                "Auto-generated credentials",
                "Secure isolated environment",
                "Connection details provided",
                "Automatic expiration",
              ].map((feature) => (
                <Box
                  key={feature}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: 20, color: "primary.main" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Right: Launch */}
        <Box sx={{ width: { xs: "100%", md: 400 } }}>
          <Paper sx={{ p: 3, position: "sticky", top: 100 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "text.primary",
              }}
            >
              Launch Instance
            </Typography>

            {!canCreate && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                You have reached the maximum of {MAX_ACTIVE_INSTANCES} active
                instances. Terminate an existing instance to create a new one.
              </Alert>
            )}

            <DurationSelector
              options={durationOptions}
              selectedHours={selectedDuration}
              onSelect={setSelectedDuration}
              disabled={!canCreate}
            />

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleLaunch}
                disabled={!canCreate || !selectedDuration || isCreating}
                sx={{ py: 1.5 }}
              >
                {isCreating
                  ? "Creating..."
                  : canCreate
                    ? "Launch Instance"
                    : "Instance Limit Reached"}
              </Button>
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                textAlign: "center",
                mt: 2,
              }}
            >
              {activeCount} / {MAX_ACTIVE_INSTANCES} instances active
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceDetailPage;
